'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { usePathname } from 'next/navigation';
import type { IconType } from '@/app/components/icons';
import { Icon } from '@/app/components/icons';
import { IconOpenInNew } from '@/app/components/icons/ui-actions/icon-open-in-new';
import { ContainerStyled } from '@/app/components/layouts/container';
import { MenuNewBadge } from '@/app/components/layouts/top-navigation/menu-new-badge';
import { TOP_NAV_DATA_GA, TOP_NAV_DATA_TEST } from '@/app/components/layouts/top-navigation/tracking';
import { Link } from '@/app/components/navigation/link';
import type { MenuLinkType } from '@/app/types/top-navigation';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { stripHtmlTags } from '@/app/utils/format';
export const MenuItemStyled = cnva('transition-color relative flex cursor-pointer items-center py-1.5 text-left outline-none hover:text-red-500 focus-visible:text-red-500', {
  variants: {
    noContainer: {
      true: '',
      false: ContainerStyled({
        size: 'mobile-only',
        maxWidth: 'none'
      })
    },
    isOpenNewTab: {
      true: 'gap-x-2',
      false: ''
    },
    hasIcon: {
      true: 'justify-between gap-x-2',
      false: ''
    }
  },
  defaultVariants: {
    isOpenNewTab: false,
    noContainer: false,
    hasIcon: false
  }
});
interface MenuItemProps extends VariantProps<typeof MenuItemStyled> {
  item: MenuLinkType;
  icon?: IconType;
  className?: string;
  onClickNext?: () => void;
  onClose?: () => void;
}
const getLabelGaText = (item: MenuLinkType) => item.labelHtml ? stripHtmlTags(item.labelHtml) : item.label;
const Label = t0 => {
  const $ = _c(4);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.label || $[1] !== item.labelHtml || $[2] !== item.newBadge) {
    t1 = item.labelHtml ? <div className="where-span:text-inline-wrap" dangerouslySetInnerHTML={{
      __html: item.labelHtml
    }} /> : <>{item.label}{!!item.newBadge && <MenuNewBadge />}</>;
    $[0] = item.label;
    $[1] = item.labelHtml;
    $[2] = item.newBadge;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const MenuItem = t0 => {
  const $ = _c(41);
  const {
    item,
    icon,
    noContainer: t1,
    className,
    onClickNext,
    onClose
  } = t0;
  const noContainer = t1 === undefined ? false : t1;
  const pathname = usePathname();
  const t2 = !!icon;
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== icon || $[2] !== item || $[3] !== noContainer || $[4] !== onClickNext || $[5] !== onClose || $[6] !== pathname || $[7] !== t2) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const styleConfigs = {
        hasIcon: t2,
        noContainer,
        className
      };
      if (typeof onClickNext === "function" || pathname === item.href) {
        const t10 = MenuItemStyled(styleConfigs);
        let t11;
        if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = createDataAttributes({
            test: TOP_NAV_DATA_TEST.navigationMenuLink
          });
          $[16] = t11;
        } else {
          t11 = $[16];
        }
        let t12;
        if ($[17] !== item) {
          t12 = createDataGA({
            click: true,
            eventName: TOP_NAV_DATA_GA.topNavigation,
            text: getLabelGaText(item)
          });
          $[17] = item;
          $[18] = t12;
        } else {
          t12 = $[18];
        }
        let t13;
        if ($[19] !== item) {
          t13 = <Label item={item} />;
          $[19] = item;
          $[20] = t13;
        } else {
          t13 = $[20];
        }
        let t14;
        if ($[21] !== icon) {
          t14 = !!icon && <Icon icon={icon} />;
          $[21] = icon;
          $[22] = t14;
        } else {
          t14 = $[22];
        }
        t9 = <button className={t10} role="menuitem" {...t11} {...t12} onClick={pathname === item.href ? onClose : onClickNext}>{t13}{t14}</button>;
        break bb0;
      }
      T0 = Link;
      t3 = item.href;
      t4 = item.target;
      t5 = "menuitem";
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = createDataAttributes({
          test: TOP_NAV_DATA_TEST.navigationMenuLink
        });
        $[23] = t6;
      } else {
        t6 = $[23];
      }
      if ($[24] !== item) {
        t7 = createDataGA({
          click: true,
          eventName: TOP_NAV_DATA_GA.topNavigation,
          text: getLabelGaText(item),
          url: item.href
        });
        $[24] = item;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      t8 = MenuItemStyled({
        ...styleConfigs,
        isOpenNewTab: item.target === "_blank"
      });
    }
    $[0] = className;
    $[1] = icon;
    $[2] = item;
    $[3] = noContainer;
    $[4] = onClickNext;
    $[5] = onClose;
    $[6] = pathname;
    $[7] = t2;
    $[8] = T0;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[26] !== item) {
    t10 = <Label item={item} />;
    $[26] = item;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== icon || $[29] !== item.target) {
    t11 = item.target === "_blank" ? <Icon icon={<IconOpenInNew />} /> : !!icon && <Icon icon={icon} />;
    $[28] = icon;
    $[29] = item.target;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== T0 || $[32] !== t10 || $[33] !== t11 || $[34] !== t3 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8) {
    t12 = <T0 href={t3} target={t4} role={t5} {...t6} {...t7} className={t8}>{t10}{t11}</T0>;
    $[31] = T0;
    $[32] = t10;
    $[33] = t11;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
};